import { useCallback, useEffect, useRef, useState } from "react";
import {
  useVoicemailMetadata,
  VoicemailMetadataStatus,
} from "./VoicemailMetadata";

export type VoicemailApi = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
export type VoicemailMessage = {
  id: string;
  folder: "INBOX" | "Old";
  receivedAt: string | null;
  durationSeconds: number | null;
};
export type VoicemailFilter = "new" | "saved" | "all";
export function voicemailCounts(messages: VoicemailMessage[]) {
  return {
    new: messages.filter((message) => message.folder === "INBOX").length,
    saved: messages.filter((message) => message.folder === "Old").length,
    total: messages.length,
  };
}
export function voicemailInbox(
  messages: VoicemailMessage[],
  filter: VoicemailFilter,
) {
  return messages
    .filter(
      (message) =>
        filter === "all" ||
        message.folder === (filter === "new" ? "INBOX" : "Old"),
    )
    .sort((first, second) => {
      const firstTime = first.receivedAt
        ? Date.parse(first.receivedAt)
        : -Infinity;
      const secondTime = second.receivedAt
        ? Date.parse(second.receivedAt)
        : -Infinity;
      return firstTime === secondTime
        ? first.id.localeCompare(second.id)
        : firstTime > secondTime
          ? -1
          : 1;
    });
}
export type DepartmentMailbox = {
  lineId: string;
  optionKey: string;
  label: string;
  messages: VoicemailMessage[];
};
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const invalid = () =>
  new Error(
    "The voicemail response was incomplete. Refresh before trying again.",
  );
export function voicemailRecord(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
export function voicemailText(value: unknown, max: number): value is string {
  return (
    typeof value === "string" &&
    value.length <= max &&
    Array.from(value).every(
      (character) =>
        character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127,
    )
  );
}
export function voicemailTimestamp(value: unknown): value is string | null {
  return (
    value === null ||
    (typeof value === "string" &&
      value.length <= 64 &&
      /^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/.test(value) &&
      Number.isFinite(Date.parse(value)))
  );
}
export function parseVoicemailMessages(
  value: unknown,
  department = false,
): VoicemailMessage[] {
  if (!Array.isArray(value) || value.length > 200) throw invalid();
  const ids = new Set<string>();
  return value.map((message: unknown) => {
    if (
      !voicemailRecord(message) ||
      Object.keys(message).length !== 4 ||
      typeof message.id !== "string" ||
      !(department ? /^[0-9a-f]{64}$/ : /^[A-Za-z0-9_-]{1,128}$/).test(
        message.id,
      ) ||
      ids.has(message.id) ||
      !["INBOX", "Old"].includes(String(message.folder)) ||
      !voicemailTimestamp(message.receivedAt) ||
      !(
        message.durationSeconds === null ||
        (typeof message.durationSeconds === "number" &&
          Number.isFinite(message.durationSeconds) &&
          message.durationSeconds >= 0 &&
          message.durationSeconds <= 3600)
      )
    )
      throw invalid();
    ids.add(message.id);
    return message as VoicemailMessage;
  });
}
export function parseDepartmentMailboxes(value: unknown): DepartmentMailbox[] {
  if (
    !voicemailRecord(value) ||
    Object.keys(value).length !== 3 ||
    Object.keys(value).some(
      (key) => !["items", "lines", "mailboxes"].includes(key),
    ) ||
    !Array.isArray(value.items) ||
    value.items.length > 1000 ||
    !Array.isArray(value.lines) ||
    value.lines.length > 1000 ||
    !Array.isArray(value.mailboxes) ||
    value.mailboxes.length > 200
  )
    throw invalid();
  const ids = new Set<string>();
  return value.mailboxes.map((box: unknown) => {
    if (
      !voicemailRecord(box) ||
      Object.keys(box).length !== 4 ||
      typeof box.lineId !== "string" ||
      !uuid.test(box.lineId) ||
      typeof box.optionKey !== "string" ||
      !/^[a-z0-9_-]{1,40}$/.test(box.optionKey) ||
      !voicemailText(box.label, 80)
    )
      throw invalid();
    const key = `${box.lineId}:${box.optionKey}`;
    if (ids.has(key)) throw invalid();
    ids.add(key);
    return {
      lineId: box.lineId,
      optionKey: box.optionKey,
      label: box.label,
      messages: parseVoicemailMessages(box.messages, true),
    };
  });
}
export function parsePrivateVoicemailAudio(
  value: unknown,
  expected: { lineId: string; messageId: string; optionKey?: string },
): string {
  if (
    !voicemailRecord(value) ||
    Object.keys(value).length !== (expected.optionKey === undefined ? 4 : 5) ||
    value.lineId !== expected.lineId ||
    value.messageId !== expected.messageId ||
    (expected.optionKey !== undefined &&
      value.optionKey !== expected.optionKey) ||
    value.mimeType !== "audio/wav" ||
    typeof value.base64 !== "string" ||
    value.base64.length < 60 ||
    value.base64.length > 2_900_000 ||
    value.base64.length % 4 !== 0 ||
    !/^[A-Za-z0-9+/]+={0,2}$/.test(value.base64)
  )
    throw invalid();
  const header = atob(value.base64.slice(0, 60));
  if (header.slice(0, 4) !== "RIFF" || header.slice(8, 12) !== "WAVE")
    throw invalid();
  return value.base64;
}
export function releaseVoicemailAudio(node: HTMLAudioElement) {
  if (!node.paused) node.pause();
  node.removeAttribute("src");
}

export function DepartmentVoicemailPanel({
  api,
  accountId,
  customer,
  csrf,
  readOnly = false,
  refreshVersion = 0,
}: {
  api: VoicemailApi;
  accountId: string;
  customer: boolean;
  csrf: string | undefined;
  readOnly?: boolean;
  refreshVersion?: number;
}) {
  const base = `/${customer ? "customer" : "admin"}/accounts/${accountId}/ivrs`;

  const [audio, setAudio] = useState<{
    client: VoicemailApi;
    scope: string;
    key: string;
    base64: string;
  } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [filter, setFilter] = useState<VoicemailFilter>("new");
  const generation = useRef(0),
    working = useRef(false),
    request = useRef<AbortController | null>(null);
  const audioElement = useRef<HTMLAudioElement | null>(null);
  const bindAudio = useCallback((node: HTMLAudioElement | null) => {
    if (audioElement.current && audioElement.current !== node)
      releaseVoicemailAudio(audioElement.current);
    audioElement.current = node;
  }, []);
  useEffect(() => setFilter("new"), [base]);
  useEffect(() => {
    const hide = () => {
      ++generation.current;
      request.current?.abort();
      request.current = null;
      working.current = false;
      setBusy(false);
      setAudio(null);
    };
    window.addEventListener("blur", hide);
    window.addEventListener("pagehide", hide);
    document.addEventListener("visibilitychange", hide);
    return () => {
      hide();
      window.removeEventListener("blur", hide);
      window.removeEventListener("pagehide", hide);
      document.removeEventListener("visibilitychange", hide);
    };
  }, []);
  const metadata = useVoicemailMetadata(
    api,
    base,
    parseDepartmentMailboxes,
    refresh,
    readOnly,
    refreshVersion,
  );
  useEffect(() => {
    ++generation.current;
    request.current?.abort();
    working.current = false;
    setBusy(false);
    setAudio(null);
    setError("");
    return () => {
      request.current?.abort();
      ++generation.current;
    };
  }, [api, base, refresh, readOnly]);
  async function listen(box: DepartmentMailbox, message: VoicemailMessage) {
    if (readOnly || working.current || !metadata.data) return;
    working.current = true;
    setBusy(true);
    setAudio(null);
    setError("");
    const current = ++generation.current,
      abort = new AbortController();
    request.current = abort;
    const timeout = setTimeout(() => {
      abort.abort();
      if (generation.current === current) {
        setError("The recording did not respond. Try listening again.");
        request.current = null;
        working.current = false;
        setBusy(false);
      }
    }, 18_000);
    try {
      const value = await api<unknown>(`${base}/audio`, {
        method: "POST",
        csrf,
        signal: abort.signal,
        body: {
          lineId: box.lineId,
          optionKey: box.optionKey,
          messageId: message.id,
        },
      });
      if (generation.current !== current || abort.signal.aborted) return;
      setAudio({
        client: api,
        scope: base,
        key: `${box.lineId}:${box.optionKey}:${message.id}`,
        base64: parsePrivateVoicemailAudio(value, {
          lineId: box.lineId,
          optionKey: box.optionKey,
          messageId: message.id,
        }),
      });
    } catch (cause) {
      if (generation.current === current)
        setError(
          abort.signal.aborted
            ? "The recording did not respond. Try listening again."
            : (cause as Error).message,
        );
    } finally {
      clearTimeout(timeout);
      if (request.current === abort) {
        working.current = false;
        setBusy(false);
        request.current = null;
      }
    }
  }
  const boxes = metadata.data;
  const counts = boxes
    ? voicemailCounts(boxes.flatMap((box) => box.messages))
    : null;
  return (
    <section className="cr-device-setup" aria-label="Department voicemail">
      <h3>Department voicemail</h3>
      <p>
        Messages left through your phone menu appear here, newest first. New and
        Saved are folders on the phone server. Listening here does not move a
        message to Saved.
      </p>
      <VoicemailMetadataStatus
        checkedAt={metadata.checkedAt}
        loading={metadata.loading}
        error={metadata.error}
      />
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {!boxes && metadata.loading && (
        <p role="status">Loading department voicemail…</p>
      )}
      {boxes?.length === 0 && (
        <p>No department mailboxes are configured for this account.</p>
      )}
      {readOnly && (
        <p>Read-only customer view. Exit before opening private recordings.</p>
      )}
      {counts && boxes && boxes.length > 0 && (
        <>
          <p>
            {boxes.length} department mailbox{boxes.length === 1 ? "" : "es"} ·{" "}
            {counts.new} new · {counts.saved} saved · {counts.total} total
          </p>
          <label className="cr-field">
            <span>Show department messages</span>
            <select
              value={filter}
              onChange={(event) => {
                ++generation.current;
                request.current?.abort();
                request.current = null;
                working.current = false;
                setBusy(false);
                setAudio(null);
                setError("");
                setFilter(event.target.value as VoicemailFilter);
              }}
            >
              <option value="new">New ({counts.new})</option>
              <option value="saved">Saved ({counts.saved})</option>
              <option value="all">All ({counts.total})</option>
            </select>
          </label>
        </>
      )}
      {boxes?.map((box) => {
        const messages = voicemailInbox(box.messages, filter),
          mailboxCounts = voicemailCounts(box.messages);
        return (
          <section
            key={`${box.lineId}:${box.optionKey}`}
            aria-label={`${box.label || "Department"} mailbox`}
          >
            <h4>{box.label || "Department mailbox"}</h4>
            <p>
              {mailboxCounts.new} new · {mailboxCounts.saved} saved ·{" "}
              {mailboxCounts.total} total
            </p>
            {!box.messages.length && (
              <p>No messages recorded in this department.</p>
            )}
            {box.messages.length > 0 && messages.length === 0 && (
              <p>
                No {filter === "new" ? "new" : "saved"} messages in this
                department. Choose another folder above.
              </p>
            )}
            <ul className="cr-message-list">
              {messages.map((message) => (
                <li key={message.id}>
                  <p>
                    <strong>
                      {message.folder === "INBOX"
                        ? "New message"
                        : "Saved message"}
                    </strong>
                    {message.receivedAt
                      ? ` · ${new Date(message.receivedAt).toLocaleString()}`
                      : " · Time unavailable"}
                    {message.durationSeconds !== null
                      ? ` · ${Math.round(message.durationSeconds)} seconds`
                      : ""}
                  </p>
                  {!readOnly && (
                    <button
                      type="button"
                      className="cr-button secondary"
                      disabled={busy}
                      onClick={() => void listen(box, message)}
                    >
                      Listen to department message
                    </button>
                  )}
                  {!readOnly &&
                    audio?.client === api &&
                    audio.scope === base &&
                    audio.key ===
                      `${box.lineId}:${box.optionKey}:${message.id}` && (
                      <div>
                        <audio
                          ref={bindAudio}
                          className="cr-audio"
                          controls
                          preload="none"
                          aria-label={`Voicemail for ${box.label || "department"}`}
                          src={`data:audio/wav;base64,${audio.base64}`}
                        />
                        <button
                          type="button"
                          className="cr-button secondary"
                          onClick={() => {
                            ++generation.current;
                            setAudio(null);
                          }}
                        >
                          Close department recording
                        </button>
                      </div>
                    )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      <button
        type="button"
        className="cr-button secondary"
        disabled={busy}
        onClick={() => setRefresh((value) => value + 1)}
      >
        Refresh department voicemail
      </button>
    </section>
  );
}
